/**
 * Let people paste a phone number into the masked tel input.
 *
 * The mask only accepts bare digits, so pasting a number copied from WhatsApp or from the
 * contacts app ("+39 340 123 4567", "340-123-4567", "0039 340...") silently did nothing and
 * the number had to be retyped by hand.
 *
 * Two cases, deliberately different (feedback 30-07-2026):
 *
 *   pasted WITH an international prefix ("+34 690...", "0034 690...")
 *       -> it wins: the country selector follows the pasted number.
 *
 *   pasted WITHOUT a prefix ("340 123 4567")
 *       -> the country currently selected is KEPT and used as prefix.
 *          Writing a bare national number into the model left the component unable to work
 *          out the country, so the selector went empty and grey.
 */

const DEFAULT_PREFIX = '+39' // same default country as main.js (defaultCountry: 'IT')

/** Digits only, plus a leading + if the number is international. */
export const normalizePhone = (raw) => {
  if (!raw) {
    return ''
  }

  let cleaned = String(raw).replace(/[^\d+]/g, '')
  cleaned = cleaned.replace(/(?!^)\+/g, '') // a plus is meaningful only in first position

  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}` // 0034... -> +34...
  }

  return cleaned
}

export const hasInternationalPrefix = (raw) => {
  const compact = String(raw || '').replace(/[^\d+]/g, '')
  return compact.startsWith('+') || compact.startsWith('00')
}

/**
 * Read the country prefix the selector is showing right now (e.g. "+39").
 * The selector lives next to the input inside the same q-tel-input wrapper, so we look for the
 * first "+NN" in the component's text. Falls back to the default country.
 */
const selectedPrefixFrom = (event) => {
  const root =
    event?.target?.closest?.('.q-tel-input') ||
    event?.target?.closest?.('.q-field')?.parentElement ||
    null

  const match = (root?.textContent || '').match(/\+\d{1,4}/)
  return match ? match[0] : DEFAULT_PREFIX
}

export const usePhonePaste = (setValue) => {
  const onPhonePaste = (event) => {
    const pasted = (event.clipboardData || window.clipboardData)?.getData('text') || ''
    if (!pasted.trim()) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (hasInternationalPrefix(pasted)) {
      setValue(normalizePhone(pasted)) // the pasted country wins
      return
    }

    // No prefix in what was pasted: keep the country already chosen.
    const digits = String(pasted).replace(/\D/g, '').replace(/^0+/, '') // drop the national trunk "0"
    setValue(`${selectedPrefixFrom(event)}${digits}`)
  }

  return { onPhonePaste }
}
